import { ConnectionManager } from '@ikea-aoa/component-view-3d';
import {
	FamilyType,
	ConnectableObject,
} from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import { ModuleConnectionManager } from './ModuleConnectionManager';

export class ConnectionKitsManager {
	constructor(
		private connectionManager: ConnectionManager,
		private moduleManager: ModuleConnectionManager,
		private sceneItems: Map<string, ConnectableObject>
	) {}

	public hasAvailableConnectionKits(
		childArticleId: string, // child article
		parent: ConnectableObject, // parent object
		child?: ConnectableObject | null, // child object
		useSimpleConnectKitLogic?: boolean // use simple logic
	): boolean {
		if (useSimpleConnectKitLogic) {
			return this.hasAvailableConnectionKitsSimple(childArticleId, parent);
		}

		let nrOfConnectionKits = 0;

		const parentConnectionKits = this.getConnectionKitsForProduct(parent.articleId);
		const childConnectionKits = this.getConnectionKitsForProduct(childArticleId);

		// There is no special connection kits logic
		if (!parentConnectionKits || !childConnectionKits) {
			return true;
		}

		const parentFamily = this.connectionManager.getFamily(parent.articleId);
		const childFamily = this.connectionManager.getFamily(childArticleId);

		if (childFamily && parentFamily) {
			let connectedItems = this.getConnected(parent.id); // get connections of parent
			if (child) {
				// if object, get connections of child item
				const connectedChildChildren = this.getConnected(child.id);
				connectedItems = connectedItems.concat(connectedChildChildren);
			}

			// remove duplicate ids
			connectedItems = Array.from(new Set(connectedItems));

			// The total number of connection kits from both the parent and the child
			nrOfConnectionKits = parentConnectionKits[childFamily] + childConnectionKits[parentFamily];

			// Check if there are any used kits because of already connected objects being the same family
			connectedItems.forEach((connectedChild) => {
				const childObject = this.sceneItems.get(connectedChild);
				if (!childObject) {
					// Object does not exist
					return;
				}
				const connectedChildProductId = childObject.articleId;
				const connectedChildFamily = this.connectionManager.getFamily(connectedChildProductId);

				if (connectedChildFamily && parentFamily && connectedChildFamily === childFamily) {
					nrOfConnectionKits = this.getReducedConnectionKitsAmount(
						nrOfConnectionKits,
						connectedChildProductId,
						connectedChildFamily,
						parentFamily
					);
				} else if (connectedChildFamily && childFamily && connectedChildFamily === parentFamily) {
					nrOfConnectionKits = this.getReducedConnectionKitsAmount(
						nrOfConnectionKits,
						connectedChildProductId,
						connectedChildFamily,
						childFamily
					);
				}
			});
		}

		return nrOfConnectionKits > 0;
	}

	private hasAvailableConnectionKitsSimple(
		childArticleId: string,
		parent: ConnectableObject
	): boolean {
		const parentConnectionKits = this.getConnectionKitsForProductSimple(parent.articleId);
		const childConnectionKits = this.getConnectionKitsForProductSimple(childArticleId);

		// There are available connection kits if the dragging module have at least one kit
		if (childConnectionKits && childConnectionKits > 0) {
			return true;
		}

		const parentConnectedModules = this.moduleManager.getConnectedModules(parent);

		return parentConnectionKits ? parentConnectionKits > parentConnectedModules.length : false;
	}

	private getConnectionKitsForProduct(articleId: string): {
		[key: string]: number;
	} | null {
		const modelData = this.connectionManager.getModelData(articleId);

		return modelData && modelData.logic && modelData.logic.connectionKits
			? modelData.logic.connectionKits
			: null;
	}

	private getConnectionKitsForProductSimple(articleId: string): number | null {
		const modelData = this.connectionManager.getModelData(articleId);

		return modelData && modelData.logic && modelData.logic.NrConnectKits
			? modelData.logic.NrConnectKits
			: null;
	}

	private getReducedConnectionKitsAmount(
		connectionKits: number,
		productId: string,
		family: FamilyType,
		parentFamily: string
	): number {
		const productConnectionKits = this.getConnectionKitsForProduct(productId);

		if (productConnectionKits) {
			// This is a special case because when we connect two chaises we need to use the connection kit from both modules (they get one each)
			if (family === 'CHAISE' && parentFamily === 'CHAISE') {
				connectionKits -= 2;
			} else {
				connectionKits += productConnectionKits[parentFamily] - 1;
			}
		}

		return connectionKits;
	}

	private getConnected(id: string): Array<string> {
		const children = this.connectionManager.getChildren(id);
		const modules = this.moduleManager.getConnectedModules(id).map((o) => o.id);
		return children.concat(modules);
	}
}
